import { createTheme } from '@mui/material/styles';

declare module '@mui/material/styles' {
  interface Palette {
    link?: string;
  }
  interface PaletteOptions {
    link?: string;
  }
}

type CommunityThemeConfig = {
  primary: string;
  secondary: string;
  logo: string;
  appBar?: string;
  contrastText?: string;
  avatar?: string;
  background?: string;
  text?: string;
  link?: string;
  surface?: string;
  border?: string;
  mode?: 'light' | 'dark';
};

export type CommunityPalette = {
  background: string;
  text: string;
  link: string;
  brand: string;
  surface: string;
  border: string;
};

const gaysTheme: CommunityThemeConfig = {
  primary: '#00D16C',
  secondary: '#FF4081',
  logo: 'gays.svg',
  avatar: 'gays_avatar.svg',
  appBar: '#F5F9F6',
  contrastText: '#0B0E0C',
  background: '#F5F9F6',
  text: '#0B0E0C',
  link: '#00D16C',
  surface: '#FFFFFF',
  border: '#D7DFD8',
  mode: 'light',
};

const poppenTheme: CommunityThemeConfig = {
  primary: '#F5C249',
  secondary: '#BE44FF',
  logo: 'poppen.svg',
  avatar: 'poppen_avatar.svg',
  appBar: '#0D0D0C',
  contrastText: '#FAF8F3',
  background: '#0D0D0C',
  text: '#FAF8F3',
  link: '#F5C249',
  surface: '#1C1B18',
  border: '#47443E',
  mode: 'dark',
};

const fetishTheme: CommunityThemeConfig = {
  primary: '#C2FF59',
  secondary: '#FF66FF',
  logo: 'fetish.svg',
  avatar: 'fetish_avatar.svg',
  appBar: '#121212',
  contrastText: '#F7F7F8',
  background: '#121212',
  text: '#F7F7F8',
  link: '#B6FC33',
  surface: '#1B1D1C',
  border: '#4D5650',
  mode: 'dark',
};

const communityThemes: Record<string, CommunityThemeConfig> = {
  fetisch: fetishTheme,
  fetish: fetishTheme,
  popcorn: poppenTheme,
  poppen: poppenTheme,
  'popcorn.dating': poppenTheme,
  gayscom: gaysTheme,
  gayde: gaysTheme,
  kaufmich: {
    primary: '#fff',
    secondary: '#e30060',
    logo: 'kaufmich.png',
    appBar: '#25223c',
    contrastText: '#ffffff',
    avatar: 'kaufmich_avatar.png',
  },
};

const defaultTheme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
  },
  typography: {
    fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
  },
});

const getCommunityConfig = (
  community?: string,
): CommunityThemeConfig | null => {
  if (!community || !communityThemes[community]) {
    return null;
  }

  return communityThemes[community];
};

export const createCommunityTheme = (community?: string) => {
  const communityConfig = getCommunityConfig(community);
  if (!communityConfig) {
    return defaultTheme;
  }

  if (
    communityConfig.background &&
    communityConfig.text &&
    communityConfig.link &&
    communityConfig.surface &&
    communityConfig.border
  ) {
    return createTheme({
      palette: {
        mode: communityConfig.mode ?? 'light',
        primary: {
          main: communityConfig.primary,
          contrastText: '#0B0E0C',
        },
        secondary: {
          main: communityConfig.secondary,
        },
        background: {
          default: communityConfig.background,
          paper: communityConfig.surface,
        },
        text: {
          primary: communityConfig.text,
        },
        divider: communityConfig.border,
        link: communityConfig.link,
      },
      typography: {
        fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
      },
    });
  }

  return createTheme({
    palette: {
      primary: {
        main: communityConfig.primary,
      },
      secondary: {
        main: communityConfig.secondary,
      },
    },
    typography: {
      fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
    },
  });
};

export const getCommunityLogo = (community?: string): string | null => {
  return getCommunityConfig(community)?.logo ?? null;
};

export const getCommunityAvatar = (community?: string): string | null => {
  const communityConfig = getCommunityConfig(community);
  if (!communityConfig) {
    return null;
  }

  return communityConfig.avatar || communityConfig.logo;
};

export const getCommunitySecondaryColor = (community?: string): string => {
  return getCommunityConfig(community)?.secondary ?? '#e3f2fd';
};

export const getCommunityAppBarColor = (community?: string): string | null => {
  return getCommunityConfig(community)?.appBar ?? null;
};

export const getContrastTextColor = (community?: string): string | null => {
  return getCommunityConfig(community)?.contrastText ?? null;
};

export const getCommunityPalette = (
  community?: string,
): CommunityPalette | null => {
  const communityConfig = getCommunityConfig(community);
  if (
    !communityConfig?.background ||
    !communityConfig.text ||
    !communityConfig.link ||
    !communityConfig.surface ||
    !communityConfig.border
  ) {
    return null;
  }

  return {
    background: communityConfig.background,
    text: communityConfig.text,
    link: communityConfig.link,
    brand: communityConfig.primary,
    surface: communityConfig.surface,
    border: communityConfig.border,
  };
};

export { communityThemes };

export default defaultTheme;
